import { LeadPackage, LeadType, useLeadCreateMutation } from "@gql-schema";
import { ErrorService } from "@shared/services/error-service";
import { errorMessage } from "@shared/services/flash-messages-service";
import { useLocale } from "@translations/locale-context";
import { Button } from "@ui/components/button/button";
import { throwError } from "@uxf/core/utils/throw-error";
import { Form } from "@uxf/form/form";
import { RadioGroup } from "@uxf/form/radio-group";
import { TextInput, TextInputValue } from "@uxf/form/text-input";
import { RadioGroupOption } from "@uxf/ui/radio-group";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React from "react";
import { SubmitHandler, useForm } from "react-hook-form";

type FormData = {
    email: TextInputValue;
    package?: LeadPackage;
    phone?: TextInputValue;
};

const FORM_ID = "contact-form";

export const AuditContactForm = () => {
    const { t } = useTranslation();
    const { push } = useRouter();
    const [createLead] = useLeadCreateMutation();
    const locale = useLocale();
    const isEn = locale === "en";

    const formApi = useForm<FormData>();

    const onSubmit: SubmitHandler<FormData> = async (values) => {
        try {
            await createLead({
                variables: {
                    lead: {
                        email: values.email ?? throwError("email is required"),
                        message: null,
                        name: "",
                        package: values.package,
                        phone: values.phone,
                        type: LeadType.UxAudit,
                    },
                },
            });
            await push(isEn ? "/en/book-consultation/thank-you" : "/sjednat-konzultaci/dekujeme");
        } catch (e) {
            ErrorService.logError(e);
            errorMessage(t("error.cantSendForm"));
        }
    };
    const options: RadioGroupOption[] = [
        {
            id: "START",
            label: "Start",
        },
        {
            id: "MEDIUM",
            label: "Medium",
        },
        {
            id: "PLATINUM",
            label: "Platinum",
        },
    ];

    return (
        <Form formApi={formApi} className="w-full" onSubmit={onSubmit} id={FORM_ID}>
            <div className="pb-5 lg:pb-12 xl:max-w-[1000px]">
                <div className="pb-10 lg:pb-0">
                    <div className="flex flex-col md:flex-row md:gap-10">
                        <TextInput
                            className="mb-10 w-full"
                            control={formApi.control}
                            form={FORM_ID}
                            isRequired
                            label={t("contact.form.email.label")}
                            name="email"
                            placeholder="@"
                            type="email"
                        />
                        <TextInput
                            className="mb-10 w-full"
                            control={formApi.control}
                            form={FORM_ID}
                            label={t("contact.form.phone.label")}
                            name="phone"
                            type="tel"
                        />
                    </div>
                    <div className="flex flex-col justify-between gap-6 lg:flex-row">
                        <RadioGroup
                            className="hidden md:block"
                            control={formApi.control}
                            id="radiogroup"
                            isRequired
                            label={locale === "en" ? "Selected package" : "Vyberte balíček auditu"}
                            name="package"
                            options={options}
                            variant="row"
                        />
                        <RadioGroup
                            className="md:hidden"
                            control={formApi.control}
                            id="radiogroup"
                            isRequired
                            label={locale === "en" ? "Selected package" : "Vyberte balíček auditu"}
                            name="package"
                            options={options}
                            radioSize="lg"
                            variant="list"
                        />
                        <Button
                            className="max-md:text-body"
                            id="contact-form-submit"
                            size="small"
                            type="submit"
                            variant="orange"
                        >
                            <p className="px-7">{t("contact.audit.form.button")}</p>
                        </Button>
                    </div>
                </div>
            </div>
        </Form>
    );
};
